import type { LegacyList, OrderCreateParams, OrderModifyParams } from '@mypoz/shared';
import { invoiceUrlFrom, readPromoDiscountCents } from '@mypoz/shared';

import { legacyApi } from '@/lib/legacy-api.js';

/**
 * Raised when an orders endpoint answers with anything but the payload it promised.
 *
 * Carries the vendor's own code so the console names the failure — `HTTP_502`, `TIMEOUT`,
 * `MALFORMED`, or one of the endpoint's documented business codes — while the screen shows a
 * translated line and a retry. Same split `PackagesRequestError` draws, and for the same reason:
 * the vendor's `message` is Chinese prose from its own code table, useful in a log and wrong in
 * front of a customer.
 */
export class OrdersRequestError extends Error {
  constructor(
    readonly endpoint: string,
    readonly code: string,
    /**
     * The failure text on its own. For `order_modify`'s status 0 this is the vendor's `msg`, the
     * only place that endpoint says *why* it refused, so the edit screen shows it beneath its own
     * translated line — the same split `PackagesRequestError.detail` draws.
     */
    readonly detail: string,
  ) {
    super(`${endpoint} failed (${code}): ${detail}`);
    this.name = 'OrdersRequestError';
  }
}

/**
 * How many summary rows to ask for in one go.
 *
 * `order_list` will not take `limit: -1` the way `package_list` does — it answers an HTML error
 * page, which is a parse failure and an empty screen rather than an error the client can name. So
 * the whole list is fetched with a ceiling instead.
 *
 * Whether the ceiling was hit is judged on the rows returned, not on the `count` the response
 * reports: that count is unreliable by one on other endpoints (310 against 309 rows on
 * `package_list`), and comparing against it would fire a needless second request on every load
 * forever. A response short of the ceiling was not truncated, whatever it claims to be counting.
 */
const PAGE_CEILING = 1000;

/**
 * Every order on the account, in the order the vendor returns them.
 *
 * Client-side filtering, for the reason `fetchPackageRows` gives: `packages/shared` already owns
 * the filter, sort and per-status counting, it is unit-tested, and pushing it to the vendor means
 * a round trip per keystroke plus one query per tab count.
 */
export async function fetchOrderRows(): Promise<readonly unknown[]> {
  const first = await legacyApi.call<LegacyList<unknown>>('order_list', { limit: PAGE_CEILING });
  if (!first.ok) throw new OrdersRequestError('order_list', first.code, first.message);

  if (first.data.list.length < PAGE_CEILING) return first.data.list;

  // The ceiling was hit, so there may be more. `count` is only a hint here, and one row of slack
  // covers its known off-by-one rather than losing the last order on the account.
  const all = await legacyApi.call<LegacyList<unknown>>('order_list', {
    limit: Math.max(first.data.count, PAGE_CEILING) + 1,
  });
  if (!all.ok) throw new OrdersRequestError('order_list', all.code, all.message);

  return all.data.list;
}

/** One request's rows and the vendor's count for its filter (MYP-126). */
export interface OrderPage {
  readonly rows: readonly unknown[];
  readonly count: number;
}

/** One `order_list` request with the given filters and paging — see `orderListParams`. */
export async function fetchOrderPage(params: Record<string, string>): Promise<OrderPage> {
  const result = await legacyApi.call<LegacyList<unknown>>('order_list', params);
  if (!result.ok) throw new OrdersRequestError('order_list', result.code, result.message);
  return { rows: result.data.list, count: result.data.count };
}

/**
 * One order by its full order number (MYP-126 search), or undefined when there is none.
 *
 * `orderno` puts `order_list` in detail mode — checked live: the answer is that order's detail
 * record, not a list — so the record is read with `normaliseOrderDetail`. A record naming another
 * order is treated as no match rather than shown under the wrong number.
 */
export async function fetchOrderByNumber(orderNumber: string): Promise<unknown> {
  const result = await legacyApi.call<LegacyList<unknown>>('order_list', { orderno: orderNumber });
  if (!result.ok) throw new OrdersRequestError('order_list', result.code, result.message);
  const [row] = result.data.list;
  if (typeof row !== 'object' || row === null) return undefined;
  return String((row as Record<string, unknown>)['orderno'] ?? '') === orderNumber
    ? row
    : undefined;
}

/**
 * The vendor's status dictionary — eighteen codes with a Chinese and an English label each.
 *
 * Fetched rather than hardcoded for the same reason the package dictionary is: the document names
 * a handful of these and the list is the vendor's to extend. `toOrderStatus` still has to know the
 * codes to group them, but the words shown to a customer come from here.
 */
export async function fetchOrderStatusRows(): Promise<readonly unknown[]> {
  const result = await legacyApi.call<LegacyList<unknown>>('order_status_list');

  if (!result.ok) throw new OrdersRequestError('order_status_list', result.code, result.message);

  return result.data.list;
}

/**
 * One order's detail record.
 *
 * Fetched per order when a card is expanded, never alongside the list. The summary rows carry no
 * packages, no address and no timeline, and there are 284 orders on the test account — pre-fetching
 * detail for all of them is 284 requests to render one screen.
 *
 * `orderid` is the vendor's own row id and the only key that returns a detail record: passing
 * `orderno` filters the *list* and gives back another summary row, which looks like it worked and
 * carries none of the fields this is called for.
 *
 * The record comes back as a bare object rather than a list, which `normaliseEnvelope` presents as
 * a one-row list — the same shape `package_list` uses for its own detail mode.
 */
export async function fetchOrderDetailRow(vendorOrderId: string): Promise<unknown> {
  const result = await legacyApi.call<LegacyList<unknown>>('order_list', {
    orderid: vendorOrderId,
  });

  if (!result.ok) throw new OrdersRequestError('order_list', result.code, result.message);

  const [row] = result.data.list;
  if (row === undefined) {
    throw new OrdersRequestError('order_list', 'EMPTY', `No order found for id ${vendorOrderId}`);
  }

  return row;
}

/**
 * `order_modify` (MYP-86) — saves an edit to an existing order.
 *
 * The answer is a bare `{status, msg}`: 1 saved, 0 for every kind of refusal (nothing changed, the
 * status moved on, the status does not allow edits, a service failed), with the reason only in
 * `msg`. Nothing comes back to apply locally, so the caller re-reads the order afterwards.
 */
export async function modifyOrder(params: OrderModifyParams): Promise<void> {
  const result = await legacyApi.call<{ message: string }>('order_modify', params);
  if (!result.ok) throw new OrdersRequestError('order_modify', result.code, result.message);
}

/**
 * `orders_create` (MYP-91) — places a new order and returns the number the vendor gave it.
 *
 * The reply is `{code, OrderNo, msg_cn}`: no order record to show, so the caller re-reads the list
 * afterwards. Returns undefined when the vendor said "created" but sent no number — the order
 * exists, and inventing a number for it would be the one thing worse than not naming it.
 *
 * A refusal throws with the vendor's code. Codes 3–9 and -1/-2 are each one lumped line in the
 * document, so `detail` carries the vendor's own `msg_cn` for them — the only place that says
 * which failure it was.
 */
export async function createOrder(params: OrderCreateParams): Promise<string | undefined> {
  const result = await legacyApi.call<Record<string, unknown>>('orders_create', params);
  if (!result.ok) throw new OrdersRequestError('orders_create', result.code, result.message);

  const orderNo = result.data['OrderNo'];
  if (typeof orderNo === 'string' && orderNo.trim() !== '') return orderNo.trim();
  if (typeof orderNo === 'number' && Number.isFinite(orderNo)) return String(orderNo);
  return undefined;
}

/**
 * `orders_pay` (MYP-92) — pays an order from the wallet with the customer's payment password.
 *
 * The reply is a bare message: nothing comes back to apply locally, so the caller re-reads the
 * order and the balance afterwards. Every answer but 1 throws with the vendor's code — 2 (wrong
 * password) and 3 (not enough balance) are ordinary outcomes the dialog shows in their own words, not
 * errors. A promo code goes with it only when `checkPromoCode` accepted it for this order (MYP-95).
 *
 * The password is sent exactly as typed and never kept: not logged, not stored, not put in the error.
 */
export async function payOrderFromWallet(
  orderNumber: string,
  payPassword: string,
  promo?: PromoDiscount,
): Promise<void> {
  const result = await legacyApi.call<{ message: string }>('orders_pay', {
    orderno: orderNumber,
    paypassword: payPassword,
    // MYP-95: a code checked by `OrderCodenoCheck` on this order, with the discount it returned.
    ...(promo ? { codeno: promo.code, codefee: centsToDecimal(promo.discountCents) } : {}),
  });
  if (!result.ok) throw new OrdersRequestError('orders_pay', result.code, result.message);
}

/**
 * `order_receive` (MYP-93) — tells the vendor a shipped order has arrived.
 *
 * Only `orderno` is sent. The reply is a bare message, so nothing comes back to apply locally: the
 * caller re-reads the order afterwards. Every answer but 1 throws with the vendor's code; the
 * vendor's text travels in the error for the log, and the customer gets a translated line instead.
 */
export async function receiveVendorOrder(orderNumber: string): Promise<void> {
  const result = await legacyApi.call<{ message: string }>('order_receive', {
    orderno: orderNumber,
  });
  if (!result.ok) throw new OrdersRequestError('order_receive', result.code, result.message);
}

/**
 * `order_cancel` (MYP-94) — cancels an order and releases its packages back to the pool.
 *
 * Only `orderno` is sent. The reply is a bare message, so the caller re-reads the order, and the
 * packages too, since their status changes as well. Every answer but 1 throws with the vendor's
 * code (2 cancel failed, -2 no order number); the vendor's text travels in the error for the log.
 */
export async function cancelVendorOrder(orderNumber: string): Promise<void> {
  const result = await legacyApi.call<{ message: string }>('order_cancel', {
    orderno: orderNumber,
  });
  if (!result.ok) throw new OrdersRequestError('order_cancel', result.code, result.message);
}

/** A promo code `OrderCodenoCheck` accepted for one order, and the discount it returned. */
export interface PromoDiscount {
  readonly code: string;
  readonly discountCents: number;
}

/** Integer cents → the vendor's decimal string, e.g. 500 → "5.00". */
function centsToDecimal(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * `OrderCodenoCheck` (MYP-95) — checks a promo code against an order's channel and billing weight.
 *
 * Only called for an order that has been packed: before that there is no billing weight, and a
 * guessed one could return a discount that does not hold (missing-APIs B3). Returns the discount
 * on code 1. Code -1 (invalid, expired, below the minimum spend…) throws with the vendor's `msg`,
 * which is Chinese only and goes to the log, never on screen.
 */
export async function checkPromoCode(
  code: string,
  channelId: string,
  billingWeightKg: string,
): Promise<number> {
  const result = await legacyApi.call<unknown>('OrderCodenoCheck', {
    codeno: code,
    channelid: channelId,
    weight: billingWeightKg,
  });
  if (!result.ok) throw new OrdersRequestError('OrderCodenoCheck', result.code, result.message);

  const discountCents = readPromoDiscountCents(result.data);
  if (discountCents === undefined) {
    throw new OrdersRequestError('OrderCodenoCheck', 'MALFORMED', 'Valid code with no codefee');
  }
  return discountCents;
}

/**
 * `ajaxUpdateUserOrderPayment` (MYP-97) — switches an order to automatic payment, so the vendor
 * takes it from the wallet as soon as the balance covers it.
 *
 * One way only: nothing switches an order back to manual (missing-APIs A6). The reply is a bare
 * message; live, a refusal comes back as `{msg: "请选择订单"}` with no code at all, which the
 * envelope reports as code `UNKNOWN`. Any answer but 1 throws; the caller re-reads the order.
 */
export async function setVendorOrderAutoPay(orderNumber: string): Promise<void> {
  const result = await legacyApi.call<{ message: string }>('ajaxUpdateUserOrderPayment', {
    orderno: orderNumber,
  });
  if (!result.ok) {
    throw new OrdersRequestError('ajaxUpdateUserOrderPayment', result.code, result.message);
  }
}

/**
 * The download link for an order's receipt PDF (MYP-124) — `get_receipt_pdf_url`, the vendor's
 * answer to Q&A v2 question 9. Throws with the vendor's code on a refusal (-2: not this customer's
 * order), and with `NO_LINK` when the reply carries no link the app will follow (`invoiceUrlFrom`
 * accepts only https://www.mypoz.com).
 */
export async function fetchInvoiceUrl(orderNumber: string): Promise<string> {
  const result = await legacyApi.call<unknown>('get_receipt_pdf_url', { orderno: orderNumber });
  if (!result.ok) throw new OrdersRequestError('get_receipt_pdf_url', result.code, result.message);
  const url = invoiceUrlFrom(result.data);
  if (url === undefined) throw new OrdersRequestError('get_receipt_pdf_url', 'NO_LINK', '');
  return url;
}
